import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { DragDropProvider, PointerSensor } from '@dnd-kit/react';
import { isSortable } from '@dnd-kit/react/sortable';
import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { RestrictToHorizontalAxis } from '@dnd-kit/abstract/modifiers';
import { SortableItem } from 'components';
import { reorderItems } from 'utils/reorder';

const Container = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  width: fit-content;
  max-width: 100%;
  position: relative;
  padding-bottom: 6px;
`;

const ItemsWrapper = styled.div`
  display: flex;
  gap: 8px;
`;

export default function HorizontalSortableList({
  items,
  setItems,
  keyExtractor,
  renderItem,
  listFooter,
  style,
  disabled,
}) {
  const containerRef = useRef(null);

  const handleDragEnd = (event) => {
    if (event.cancelled) return;

    const { source } = event.operation;

    if (isSortable(source)) {
      const { initialIndex, index } = source;

      if (initialIndex !== index) {
        const reordered = reorderItems(items, initialIndex, index);
        setItems(reordered);
      }
    }
  };

  return (
    <DragDropProvider
      sensors={[PointerSensor]}
      modifiers={[
        RestrictToHorizontalAxis,
        RestrictToElement.configure({ element: () => containerRef.current }),
      ]}
      onDragEnd={handleDragEnd}
    >
      <Container ref={containerRef} style={{ ...style }}>
        <ItemsWrapper>
          {items.map((item, index) => (
            <SortableItem
              key={keyExtractor(item)}
              id={keyExtractor(item)}
              index={index}
              disabled={disabled}
            >
              {renderItem(item)}
            </SortableItem>
          ))}
        </ItemsWrapper>

        {listFooter}
      </Container>
    </DragDropProvider>
  );
}

HorizontalSortableList.propTypes = {
  items: PropTypes.array.isRequired,
  setItems: PropTypes.func.isRequired,
  keyExtractor: PropTypes.func.isRequired,
  renderItem: PropTypes.func.isRequired,
  listFooter: PropTypes.node,
  style: PropTypes.object,
  disabled: PropTypes.bool,
};

HorizontalSortableList.defaultProps = {
  listFooter: null,
  style: undefined,
  disabled: false,
};
